import * as React from "react";
import { FastField } from "formik";
import { Button, FormGroup, Input, RotateLoader } from "@lib/components";
import { MobxComponent } from "../../../../../../../mobx/components/index";
import { inject, observer } from "mobx-react";
import { RestaurantForm } from "../../../../../../../mobx/components/restaurant-form";
import { UI } from "../../../../../../../core/ui";

type MenuVersionSettings = NonNullable<
	T.Schema.Restaurant.RestaurantSchema["settings"]["menu_versions"]
>;

interface Props { }
interface State { }
interface FormValues {
	menu_versions: MenuVersionSettings;
}

/** The fields this form edits, and the ceilings the menu service clamps them to. */
const LIMITS = {
	per_menu_count: 50,
	max_total: 500,
	retention_days: 30,
};

type EditableField = keyof typeof LIMITS;

/** Blank means "use the default", which is how the service reads an unset field. */
function toNumber(raw: string): number | undefined {
	const value = parseInt(raw, 10);
	return Number.isFinite(value) && value > 0 ? value : undefined;
}

@inject("store") @observer
export class SettingsFormMenuVersions extends MobxComponent<Props, State> {

	initialValues: FormValues;

	constructor(props: Props) {
		super(props);
		this.state = {};
		const r = this.injected.store.restaurant!;
		this.initialValues = { menu_versions: { ...(r.settings.menu_versions || {}) } };
	}

	render() {
		return (
			<RestaurantForm<FormValues>
				submit={async (r, values) => {
					r.settings.menu_versions = values.menu_versions;
					const update = { $set: { "settings.menu_versions": values.menu_versions } };
					return { r, update };
				}}
				initialValues={this.initialValues}
				onError={() => UI.notification.error("An error occurred")}
				onSuccessMessage="Settings Updated"
			>
				{({ form, error }) => {
					const { isSubmitting, setFieldValue } = form;
					const field = (name: EditableField, title: string, help: string) => (
						<FormGroup optional={true} title={title} help={help}>
							<FastField
								name={`menu_versions.${name}`}
								render={({ field: f }: any) => (
									<Input
										type="number"
										min={1}
										max={LIMITS[name]}
										placeholder="Default"
										value={f.value ?? ""}
										onChange={(e: any) =>
											setFieldValue(`menu_versions.${name}`, toNumber(e.target.value))
										}
									/>
								)}
							/>
						</FormGroup>
					);

					return (
						<div className="p-4">
							<p className="m-b-4 lhp">
								Each time a menu changes, a version is saved so you can see what changed
								and go back to it. These settings decide how many versions this store
								keeps and for how long. Leave a field blank to use the standard setting.
							</p>

							{field("per_menu_count", "Versions to keep for each menu",
								`Every menu keeps this many of its own versions, so a day spent editing one menu does not wipe out the history of the others. Up to ${LIMITS.per_menu_count}.`)}

							{field("max_total", "Total versions to keep",
								`An overall limit for the whole store. Set it comfortably above the per-menu number multiplied by how many menus get edited, or history will disappear sooner than you expect. Up to ${LIMITS.max_total}.`)}

							{field("retention_days", "Days to keep versions",
								`Versions older than this are deleted, even when the limits above would have kept them. Up to ${LIMITS.retention_days} days.`)}

							{error && <FormGroup error={error} />}

							<Button full={true} color="primary" type="submit" disabled={isSubmitting}>
								{isSubmitting && <RotateLoader size={2} color="white" />}
								{!isSubmitting && "Save"}
							</Button>
						</div>
					);
				}}
			</RestaurantForm>
		);
	}

}
